<template>
  <div v-if="game" class="table">
    <!-- Async room: who the game is waiting on (src/store/asyncPlay.js). -->
    <p v-if="waitingBanner" class="waiting-banner" :class="{ 'is-mine': waitingOnMe }">{{ waitingBanner }}</p>
    <!-- status strip -->
    <div class="table__status">
      <span class="table__leg">Leg {{ view.leg }}</span>
      <span class="table__turnline">
        <template v-if="finished && !tumbling"><strong>{{ winnerLabel }}</strong> crosses the finish line!</template>
        <template v-else-if="finished">…</template>
        <template v-else-if="myTurn">Your action</template>
        <template v-else>{{ activePlayer?.name }} is thinking…</template>
      </span>
      <span class="table__money">
        <img src="art/coin-1.jpg" alt="" /> {{ me?.money ?? 0 }}
      </span>
    </div>

    <!-- the race -->
    <GameBoard
      :track="view.track"
      :desert="view.desert"
      :players="game.players"
      :placing="panel === 'desert'"
      :legal-spaces="legalSpaces"
      @space="chooseSpace"
    >
      <div class="oasis">
        <div class="oasis__pyramid" :title="`${pyramidCount} dice in the pyramid`">
          <svg viewBox="0 0 40 30" aria-hidden="true"><path d="M20 2 L38 28 L2 28 Z" /></svg>
          <span>{{ pyramidCount }}</span>
        </div>
        <div class="oasis__dice">
          <span
            v-for="(roll, i) in view.diceRolled"
            :key="i"
            class="rolled-die"
            :class="`rolled-die--${roll.camel}`"
          >{{ roll.value }}</span>
          <span v-if="tumbling && game.lastRoll" class="oasis__live" :class="`camel-die--${game.lastRoll.camel}`">
            <!-- A camel die is a THREE: 1, 2 or 3. Without saying so the
                 cube tumbles through fours, fives and sixes it cannot
                 roll. Matt, on Commonwealth's d3s, 2026-09-19: "it's
                 showing fives as it rolls along." -->
            <Die3d :value="game.lastRoll.value" :faces="DIE_FACES" :phase="'tumble'" :duration="tumbleMs" />
          </span>
        </div>
      </div>
    </GameBoard>

    <!-- action panels -->
    <div class="table__controls">
      <template v-if="myTurn && !tumbling">
        <div v-if="panel === null" class="action-grid">
          <button class="primary action-grid__roll" @click="roll">
            Shake the pyramid <span class="action-grid__sub">+1 EP</span>
          </button>
          <button class="secondary" @click="panel = 'leg'">Back a camel<br /><span class="action-grid__sub">this leg</span></button>
          <button class="secondary" @click="panel = 'desert'">Desert tile</button>
          <button class="secondary" @click="panel = 'bet'">Bet the race</button>
        </div>

        <div v-else-if="panel === 'leg'" class="picker">
          <p class="picker__hint">Take the top tile — it pays if that camel leads when the leg ends.</p>
          <div class="picker__row">
            <button
              v-for="camel in CAMELS"
              :key="camel"
              class="leg-tile-button"
              :disabled="!game.legTiles[camel].length"
              @click="takeTile(camel)"
            >
              <img v-if="game.legTiles[camel].length" :src="`art/leg-${camel}-${game.legTiles[camel][0]}.jpg`" :alt="`${camel} ${game.legTiles[camel][0]}`" />
              <span v-else class="leg-tile-button__gone">—</span>
            </button>
          </div>
          <button class="secondary" @click="panel = null">Back</button>
        </div>

        <div v-else-if="panel === 'desert'" class="picker">
          <p class="picker__hint">
            <template v-if="pendingSpace === null">
              Tap a highlighted space{{ game.desert[mySeat] ? ' to move your tile' : '' }}.
            </template>
            <template v-else>Space {{ pendingSpace + 1 }}: which side up?</template>
          </p>
          <div v-if="pendingSpace !== null" class="picker__row">
            <button class="desert-side" @click="placeTile('oasis')">
              <img src="art/oasis.jpg" alt="Oasis" /><span>Oasis +1</span>
            </button>
            <button class="desert-side" @click="placeTile('mirage')">
              <img src="art/mirage.jpg" alt="Mirage" /><span>Mirage −1</span>
            </button>
          </div>
          <button class="secondary" @click="panel = null; pendingSpace = null">Back</button>
        </div>

        <div v-else-if="panel === 'bet'" class="picker">
          <p class="picker__hint">
            Back the overall {{ betPile }} — face down, scored at the finish.
            <button class="picker__flip" @click="betPile = betPile === 'winner' ? 'loser' : 'winner'">
              switch to {{ betPile === 'winner' ? 'loser' : 'winner' }}
            </button>
          </p>
          <div class="picker__row">
            <button
              v-for="camel in CAMELS"
              :key="camel"
              class="bet-card-button"
              :disabled="!me?.betCards[camel]"
              @click="placeBet(camel)"
            >
              <img :src="`art/camel-${camel}.jpg`" :alt="camel" />
            </button>
          </div>
          <button class="secondary" @click="panel = null">Back</button>
        </div>
      </template>
      <p v-else-if="finished && !tumbling" class="muted table__waitline">
        Tap anywhere for the results
      </p>
      <p v-else-if="!myTurn" class="muted table__waitline">
        {{ absentNote || ' ' }}
      </p>
    </div>

    <!-- the table -->
    <div class="seats-strip">
      <div
        v-for="p in game.players"
        :key="p.id"
        class="seat-chip"
        :class="{ 'seat-chip--active': p.seat === game.turn.activeSeat, 'seat-chip--me': p.seat === mySeat }"
      >
        <span class="seat-chip__name">{{ p.name }}<template v-if="p.seat === mySeat && p.name !== 'You'"> (you)</template></span>
        <span class="seat-chip__money"><img src="art/coin-1.jpg" alt="EP" />{{ p.money }}</span>
        <span class="seat-chip__tiles">
          <img
            v-for="(tile, i) in p.legTiles"
            :key="i"
            class="seat-chip__legtile"
            :src="`art/leg-${tile.camel}-${tile.value}.jpg`"
            :alt="`${tile.camel} ${tile.value}`"
          />
          <span v-if="p.pyramidTiles" class="seat-chip__pyr">
            <img src="art/pyramid-tile.jpg" alt="pyramid tile" />×{{ p.pyramidTiles }}
          </span>
        </span>
        <span class="seat-chip__bets" title="race bets placed">
          bets {{ 5 - Object.values(p.betCards).filter(Boolean).length }}
        </span>
      </div>
    </div>

    <div class="bet-piles muted">
      <span>Winner bets: {{ game.bets.winner.length }}</span>
      <span>Loser bets: {{ game.bets.loser.length }}</span>
    </div>

    <!-- leg scoring interstitial -->
    <div v-if="showLegReport" class="overlay" @click.self="dismissLegReport">
      <div class="report">
        <h3>Leg {{ game.legReport.leg }} scored</h3>
        <!-- The payouts land one row at a time (the drama report,
             2026-08-29) - pure CSS stagger, so reduced motion shows it all
             at once, and the dismiss tap works from the first frame. -->
        <p class="report__line score-row--enter" :style="legRevealDelay(0)">
          <CamelChip :camel="game.legReport.leader" /> led the leg,
          <CamelChip :camel="game.legReport.second" /> came second.
        </p>
        <table class="report__table">
          <tbody>
            <tr
              v-for="(r, i) in game.legReport.results"
              :key="r.seat"
              class="score-row--enter"
              :style="legRevealDelay(1 + i)"
            >
              <td class="report__name">{{ r.name }}</td>
              <td class="report__delta" :class="{ 'is-up': r.delta > 0, 'is-down': r.delta < 0 }">
                {{ r.delta >= 0 ? '+' : '' }}{{ r.delta }}
              </td>
              <td class="report__money">{{ r.money }} EP</td>
            </tr>
          </tbody>
        </table>
        <button
          class="primary score-row--enter"
          :style="legRevealDelay(1 + game.legReport.results.length)"
          @click="dismissLegReport"
        >
          On to leg {{ game.legReport.leg + 1 }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { waitingLine, waitingOnMe as isWaitingOnMe } from '@/store/asyncPlay.js';
import GameBoard from '@/components/game/GameBoard.vue';
import CamelChip from '@/components/game/CamelChip.vue';
import Die3d from '@/components/game/Die3d.vue';
import { CAMELS, DIE_MIN, DIE_MAX } from '@/engine/constants';
import { legalDesertSpaces } from '@/engine/moves';
import { rollAnimationMs, dealTumbleDurations } from '@/utils/rollTheatre';
import { LEG_REVEAL_BASE_MS, LEG_REVEAL_STEP_MS } from '@/utils/scoreTheatre';

export default {
  name: 'GameTable',
  components: { GameBoard, CamelChip, Die3d },
  data() {
    return {
      // 1, 2, 3 - what a camel die can actually land on.
      DIE_FACES: Array.from({ length: DIE_MAX - DIE_MIN + 1 }, (_, i) => DIE_MIN + i),
      panel: null, // null | 'leg' | 'desert' | 'bet'
      pendingSpace: null,
      betPile: 'winner',
      tumbling: false,
      tumbleMs: 1600,
      tumbleTimer: null,
      dismissedLeg: 0,
      // The last SETTLED table - what the board shows while a die is still
      // tumbling. See the game watcher below.
      settled: null,
      lastTumbledSeq: -1,
      CAMELS,
    };
  },
  computed: {
    ...mapState(['game', 'thinkingPlayerId']),
    ...mapGetters(['myTurn', 'mySeat', 'activePlayer', 'absentPlayerIds']),
    waitingOnMe() { return isWaitingOnMe(this.$store.state.roomMeta, this.$store.state.user?.uid); },
    waitingBanner() {
      const meta = this.$store.state.roomMeta;
      if (!meta?.async || !this.game) return '';
      const nameOf = (id) => this.game.players.find((p) => p.id === id)?.name || 'someone';
      return waitingLine(meta, this.$store.state.user?.uid, nameOf);
    },
    me() { return this.game?.players[this.mySeat] || null; },
    finished() { return this.game?.meta.phase === 'finished'; },
    winnerLabel() {
      const camel = this.game?.meta.winnerCamel || '';
      return camel.charAt(0).toUpperCase() + camel.slice(1);
    },
    // What the board and dice row render: the pre-roll world while the die
    // is mid-tumble, the live state otherwise.
    view() {
      if (this.tumbling && this.settled) return this.settled;
      return this.snapshotOf(this.game);
    },
    pyramidCount() {
      // Mid-tumble the rolled die is neither in the pyramid nor settled -
      // it's the one in the air - so the count comes down by one at once.
      if (this.tumbling && this.settled) return Math.max(this.view.legDice - 1, 0);
      return this.view.legDice;
    },
    legalSpaces() {
      if (!this.game) return [];
      return legalDesertSpaces(this.game.track, this.game.desert, this.mySeat);
    },
    showLegReport() {
      return Boolean(this.game?.legReport)
        && this.game.legReport.leg > this.dismissedLeg
        && !this.tumbling
        && this.game.meta.phase === 'playing';
    },
    absentNote() {
      if (!this.absentPlayerIds.length) return '';
      const names = this.game.players
        .filter((p) => this.absentPlayerIds.includes(p.id))
        .map((p) => p.name);
      return `${names.join(', ')} disconnected — a bot fills in until they return.`;
    },
  },
  watch: {
    // One watcher owns the roll theatre AND the settled snapshot. When the
    // latest event is a roll, the board keeps showing the PRE-roll world -
    // camel unmoved, no flat result die in the row - until the tumble
    // lands, then everything snaps forward together. Before this, the camel
    // jumped and the result appeared the instant the action dispatched,
    // which spoiled the very roll the animation exists to sell (the drama
    // report, 2026-08-29). Theatre only: the engine settled it long ago.
    game: {
      handler(game) {
        if (!game) { this.settled = null; return; }
        const ms = rollAnimationMs(game); // 0 unless the latest event is a roll
        if (ms > 0 && game.meta.seq !== this.lastTumbledSeq && this.settled) {
          this.lastTumbledSeq = game.meta.seq;
          this.tumbleMs = dealTumbleDurations(
            [`${game.lastRoll.camel}${game.lastRoll.value}`],
            game.meta.seq || 0,
          )[0];
          this.tumbling = true;
          window.clearTimeout(this.tumbleTimer);
          this.tumbleTimer = window.setTimeout(() => {
            this.tumbling = false;
            this.settled = this.snapshotOf(this.game);
          }, ms);
          return;
        }
        // Anything that isn't a fresh roll (or a state seen mid-join, with
        // nothing settled to hold on to) settles immediately.
        if (!this.tumbling) this.settled = this.snapshotOf(game);
      },
      immediate: true,
    },
  },
  beforeUnmount() { window.clearTimeout(this.tumbleTimer); },
  methods: {
    snapshotOf(game) {
      if (!game) return null;
      return {
        track: game.track,
        desert: game.desert,
        diceRolled: game.diceRolled,
        legDice: game.legDice.length,
        leg: game.turn.leg,
      };
    },
    legRevealDelay(step) {
      return { animationDelay: `${LEG_REVEAL_BASE_MS + step * LEG_REVEAL_STEP_MS}ms` };
    },
    roll() { this.$store.dispatch('roll'); },
    takeTile(camel) {
      this.panel = null;
      this.$store.dispatch('takeLegTile', camel);
    },
    chooseSpace(space) { this.pendingSpace = space; },
    placeTile(side) {
      const space = this.pendingSpace;
      this.panel = null;
      this.pendingSpace = null;
      this.$store.dispatch('placeDesert', { space, side });
    },
    placeBet(camel) {
      const pile = this.betPile;
      this.panel = null;
      this.$store.dispatch('bet', { pile, camel });
    },
    dismissLegReport() {
      this.dismissedLeg = this.game?.legReport?.leg || 0;
    },
  },
};
</script>

<style scoped>
.waiting-banner { margin: 0; padding: 0.5rem 0.9rem; border-radius: 0.6rem; background: rgba(0,0,0,0.08); font-size: 0.95rem; }
.waiting-banner.is-mine { background: rgba(0,0,0,0.16); font-weight: 700; }
</style>
